import { getTheme } from './theme/config'
import { useState, useRef, useEffect, useContext } from 'react'
import Chat, { useGetAnswerV2 } from './Chat'
import { useTransition, animated, config } from '@react-spring/web'
import styled, { ThemeProvider, useTheme } from 'styled-components'
import { HiDotsHorizontal } from 'react-icons/hi'
import Placeholder from './Placeholder/'
import Examples from './Examples'
import Input from './Input'
import { GrPowerReset } from 'react-icons/gr'
import { TrackingContext } from 'contexts'
import { device } from '../../breakpoints'
import { IoMdInformationCircle } from 'react-icons/io'

const Agent = ({
  handleHideAgent,
}: {
  handleHideAgent: (hasMessages: boolean) => void
}) => {
  const t = useTheme()
  const trackEvent = useContext(TrackingContext)
  const theme = getTheme(t.colorMode, t.tenant)
  const containerRef = useRef<HTMLDivElement>(null)
  const [AISearchMode, setAISearchMode] = useState<boolean>(true)
  const [chatMessages, setChatMessages] = useState<ChatInterface[]>([])
  const [threadId, setThreadId] = useState<string>()
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false)
  const { errorMessage, setErrorMessage, getAnswer, isLoading } =
    useGetAnswerV2()

  // Messages transitions
  const transitions = useTransition(isLoading, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.molasses,
  })

  // Handle user message
  const handleNewUserMessage = (
    text: string,
    suggestedInput: boolean = false
  ) => {
    if (isLoading || text.length === 0) return

    const date = new Date()

    // Display the question in the chat
    setChatMessages((prevChatMessages) => [
      ...prevChatMessages,
      { text, id: prevChatMessages.length, type: 'user', date: date },
    ])

    // Get the answer
    getAnswer(text, threadId).then((response) => {
      setThreadId(response.threadId)
      setChatMessages((prevChatMessages) => [...prevChatMessages, response])

      // Track the search event
      trackEvent &&
        trackEvent('search', {
          search_keyword: text,
          search_type: 'streaming',
          ai_enabled: true,
          search_results: response.articles?.length || 0,
          suggested_input: suggestedInput,
        })
    })
  }

  // useEffect(() => {
  //   if (message) {
  //     setChatMessages((prevChatMessages) => [...prevChatMessages, message])
  //   }
  // }, [message])

  useEffect(() => {}, [AISearchMode])

  return (
    <Container ref={containerRef}>
      <Content>
        <ThemeProvider theme={theme}>
          <Chat messages={chatMessages} />
          <>
            {chatMessages.length === 0 && (
              <Examples
                handleClick={(exampleText) => {
                  handleNewUserMessage(exampleText, true)
                }}
              />
            )}
          </>
          {/* <>{isConnecting && <UIMessage>Connection en cours...</UIMessage>}</> */}
          <>{isLoading && <Placeholder />}</>
          <>
            {errorMessage ===
            'Message is bigger than max allowed length of 500 characters.' ? (
              <UIMessage>
                Votre message dépasse les 500 caractères autorisés. Veuillez le
                raccourcir.
              </UIMessage>
            ) : (
              errorMessage && (
                <UIMessage>
                  Le service n'est pas accessible pour le moment. Réessayez plus
                  tard!
                </UIMessage>
              )
            )}
          </>

          <UserBox>
            <Menu>
              <Message>
                {isLoading && <Icon $isLoading={isLoading} />}
                {transitions((style, items) =>
                  items ? (
                    <MessageContent style={style}>
                      Réponse en cours de génération
                    </MessageContent>
                  ) : (
                    isLoading && (
                      <MessageContent style={style}>
                        {isLoading
                          ? 'Connection en cours...'
                          : // : errorMessage
                          // ? "Une erreur s'est produite. Veuillez réessayer."
                          chatMessages.length === 0
                          ? 'Commencer la conversation'
                          : 'Continuer la conversation'}
                      </MessageContent>
                    )
                  )
                )}
              </Message>
            </Menu>
            <InputContainer>
              <Input onEnter={handleNewUserMessage} />
            </InputContainer>
            <Disclaimer>
              <IconContainer onClick={() => setShowDisclaimer(!showDisclaimer)}>
                <IoMdInformationCircle />
              </IconContainer>
              Les réponses sont basées sur nos archives. L’IA pouvant parfois
              produire des imprécisions, nous vous invitons à consulter les
              sources fournies.
              <br />
              {/* {!showDisclaimer && (
                <BtnLink onClick={() => setShowDisclaimer(!showDisclaimer)}>
                  En savoir plus
                </BtnLink>
              )} */}
              {showDisclaimer && (
                <DisclaimerMore>
                  Grâce à l’intelligence artificielle, notre chatbot vous aide à
                  explorer facilement nos critiques sur les séries TV. Nous
                  utilisons des grands modèles de langage (LLM) pour analyser
                  votre requête, sélectionner les articles dans nos archives,
                  puis générer une réponse. Gardez à l'esprit que l'IA peut
                  parfois faire des erreurs. Vous pouvez vérifier les
                  informations en consultant les articles originaux. Vous
                  trouverez davantage d’informations sur notre utilisation de
                  l'IA{' '}
                  <a
                    href='https://www.24heures.ch/lia-chez-tamedia-voici-comment-nous-utilisons-lintelligence-artificielle-339025362648'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    ici
                  </a>
                  .
                </DisclaimerMore>
              )}
            </Disclaimer>
          </UserBox>
          {chatMessages.length > 0 && (
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setChatMessages([])
                  setErrorMessage(null)
                  setThreadId(undefined)
                  containerRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                <GrPowerReset /> Nouvelle conversation
              </Button>
            </div>
          )}
        </ThemeProvider>
      </Content>
      <AIActivationBox
        style={{
          justifyContent: 'center',
        }}
      >
        <ToggleBox>
          <ToggleOption
            onClick={() => {
              setAISearchMode(!AISearchMode)
              handleHideAgent(chatMessages.length > 0)
            }}
            active={!AISearchMode}
          >
            Recherche classique
          </ToggleOption>
          <ToggleOption
            onClick={() => {
              setAISearchMode(AISearchMode)
              handleHideAgent(chatMessages.length > 0)
            }}
            active={AISearchMode}
          >
            Recherche IA
          </ToggleOption>
        </ToggleBox>
      </AIActivationBox>
    </Container>
  )
}

const Container = styled.div`
  font-family: var(--font-plex);
  font-size: 1.8rem;
  line-height: 3rem;
`

const Content = styled.div`
  box-sizing: border-box;
  background-color: var(--site-background);
  border-radius: 10px;
  box-shadow: ${({ theme }) => `0px 0px 18px ${theme.colorGrey}`};
  padding: 1.5rem;
`

const Menu = styled.div``

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
`

const AIActivationBox = styled.div`
  margin-top: 2rem;
  font-size: 1.6rem;
  font-family: var(--font-plex);
  -webkit-font-smoothing: antialiased;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ToggleBox = styled.div`
  display: flex;
  justify-content: center;
  font-size: 0.85em;
`

const ToggleOption = styled.div<{ active: boolean }>`
  padding: 0.35em 0.5em;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  background-color: ${({ theme, active }) =>
    active ? `${theme.colorText}` : 'transparent'};
  color: ${({ theme, active }) =>
    active ? 'var(--site-background)' : `${theme.colorText}`};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colorGrey};
  line-height: 1.4;

  &:hover {
    background-color: ${({ theme, active }) =>
      !active && `${theme.colorGrey}77`};
  }

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  &:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`

const UserBox = styled.div``

const BtnLink = styled.div`
  font-size: 1.35rem;
  line-height: 1.4;
  display: block;
  margin-top: 0.2em;
  color: ${({ theme }) => theme.colorText};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colorText};
  }
`

const Message = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colorText};
  display: flex;
  align-items: center;
  position: relative;
`

const UIMessage = styled.div`
  font-size: 1.5rem;
  position: relative;
  text-align: center;
  border-radius: 0px;
  padding: 1.5rem;
  margin: 2rem 0;
  color: ${({ theme }) => theme.colorTextInverted};
  background-color: ${({ theme }) => theme.colorText};
  line-height: 1.2;
`

const Button = styled.div`
  font-size: 0.85em;
  color: ${({ theme }) => theme.colorText};
  -webkit-font-smoothing: antialiased;
  cursor: pointer;
  transition: background-color 0.1s ease-in-out, color 0.1s ease-in-out;
  display: inline-flex;
  align-items: center;
  text-align: right;
  white-space: nowrap;
  line-height: 1.7;
  overflow: hidden;
  letter-spacing: 0.02rem;
  margin-left: 1rem;
  justify-content: end;
  margin-top: 1rem;
  flex-shrink: 0;
  border-radius: 6px;
  padding: 0.25rem 1.25rem;

  svg {
    margin-right: 1rem;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colorText};
    color: var(--site-background);
  }
`

const InputContainer = styled.div``

const Icon = styled(HiDotsHorizontal)<{ $isLoading: boolean }>`
  color: ${({ theme }) => theme.colorText};
  font-size: 2rem;
  animation: ${({ $isLoading }) =>
    $isLoading ? 'spin 1s linear infinite' : 'none'};

  @keyframes spin {
    from {
      opacity: 1;
    }

    to {
      opacity: 0.25;
    }
  }
`

const MessageContent = styled(animated.div)`
  position: absolute;
  left: 2.5rem;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  margin-bottom: 0.65rem;
`

const Notes = styled.div`
  font-size: 1.35rem;
  line-height: 1;
  display: block;
  white-space: nowrap;

  a,
  a:visited,
  a:active {
    color: ${({ theme }) => theme.primaryButtonText};
  }
`

const Disclaimer = styled.div`
  font-size: 1.35rem;
  line-height: 1.4;
  display: block;
  margin-top: 1rem;
  position: relative;
  color: ${({ theme }) => `${theme.colorText}77`};
  z-index: 1000;
  padding-left: 30px;
`

const DisclaimerMore = styled.div`
  margin-top: 0.5em;
`

const IconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  font-size: 1.2em;
  position: absolute;
  top: 0.25em;
  left: 0;
  height: 1rem;

  svg {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translate(0px, -50%);
  }
`

export default Agent
